import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isActivePerson, type Person } from '@domain/entities/Person';
import { celebratesOn } from '@domain/policies/BirthdayPolicy';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import { Button, Modal } from '../../design-system';
import { useAccess } from '../../hooks/useAccess';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { useSessionState } from '../../providers/SessionProvider';
import logoUrl from '../../assets/knLogo.png';
import { CakeIcon } from './CakeIcon';
import { Confetti } from './Confetti';
import styles from './BlogScreen.module.css';

/**
 * La felicitación al cumpleañero: un modal con confeti, de parte de la empresa.
 *
 * Lo pidió JC el 1 oct 2026. Sale **una vez por día** —al cerrarlo se anota en
 * el navegador y no vuelve hasta el año siguiente—, a quien entra al portal el
 * día de su cumpleaños, mire la ruta que mire. Va detrás de Kn·Blog: con el
 * módulo apagado no sale, porque es el mismo festejo.
 *
 * **Después de «Confirma tus datos», nunca encima.** Lo monta `ConfirmDataGate`
 * cuando ya no tiene nada que preguntar: dos ventanas apiladas el primer día de
 * alguien serían una bienvenida peor que ninguna.
 *
 * La fecha sale de la propia ficha, que la sesión ya trae: no cuesta una
 * consulta ni pasa por el servidor.
 */

const seenKey = (personId: string): string => `knpeople.cumple.visto.${personId}`;

/* El almacenamiento del navegador puede no estar —ventana privada, datos
   bloqueados—. Sin él, la felicitación sale cada vez que se entra ese día, que
   es un precio aceptable para un festejo. */
const readSeen = (personId: string): string | null => {
  try {
    return window.localStorage.getItem(seenKey(personId));
  } catch {
    return null;
  }
};

const markSeen = (personId: string, day: CalendarDate): void => {
  try {
    window.localStorage.setItem(seenKey(personId), day.toISO());
  } catch {
    /* Ver `readSeen`. */
  }
};

/**
 * Si hoy toca felicitar a esta persona. Suelta para fijarla sin React.
 *
 * `seenOn` es el día en que ya se le felicitó en este navegador.
 */
export const shouldGreet = (params: {
  readonly person: Pick<Person, 'status' | 'birthDate'>;
  readonly today: CalendarDate;
  readonly seenOn: string | null;
}): boolean => {
  const { person, today, seenOn } = params;
  if (!isActivePerson(person) || person.birthDate === null) return false;
  if (seenOn === today.toISO()) return false;
  return celebratesOn(person.birthDate, today);
};

const firstNameOf = (person: Pick<Person, 'displayName' | 'nameParts'>): string =>
  person.nameParts?.primerNombre ?? person.displayName.trim().split(/\s+/)[0] ?? person.displayName;

const GreetingModal = ({
  person,
  onClose,
}: {
  readonly person: Person;
  readonly onClose: () => void;
}): ReactNode => {
  const { featureOn } = useAccess();
  const { identity } = useRuntimeConfig();
  const navigate = useNavigate();
  /* La primera ráfaga es la de abrir; el botón suelta otra. */
  const [burst, setBurst] = useState(0);

  if (!featureOn('knblog')) return null;

  const company = identity.commercialName;

  return (
    <>
      <Confetti burst={burst} />
      <Modal
        title={`Un mensaje de ${company}`}
        onClose={onClose}
        closeLabel="Cerrar la felicitación"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                void navigate('/blog');
              }}
            >
              Ver Kn·Blog
            </Button>
            <Button variant="primary" onClick={onClose}>
              ¡Gracias!
            </Button>
          </>
        }
      >
        <div className={styles.greeting}>
          <img src={logoUrl} alt={company} className={styles.greetingLogo} />
          <CakeIcon className={styles.greetingCake} />
          <p className={styles.greetingTitle}>¡Feliz cumpleaños, {firstNameOf(person)}!</p>
          <p className={styles.greetingText}>
            De parte de todos en {company}: que hoy sea un gran día y que este año venga lleno de
            cosas buenas. Gracias por ser parte del equipo.
          </p>
          <button
            type="button"
            className={styles.greetingMore}
            onClick={() => {
              setBurst((n) => n + 1);
            }}
          >
            🎉 Más confeti
          </button>
        </div>
      </Modal>
    </>
  );
};

export const BirthdayGreeting = (): ReactNode => {
  const { status } = useSessionState();
  const person = status.kind === 'ready' ? status.person : null;
  const today = getServices().clock.today();
  /*
   * Quién la cerró hoy, en esta pestaña. Además de lo anotado en el navegador,
   * porque sin almacenamiento la anotación no queda y el modal volvería a abrirse
   * en cuanto se cerrara. Por persona y no un sí/no: la persona de la sesión
   * puede cambiar sin que este componente se desmonte.
   */
  const [closed, setClosed] = useState<string | null>(null);

  if (person === null) return null;
  const closedKey = `${person.id}@${today.toISO()}`;
  const seenOn = closed === closedKey ? today.toISO() : readSeen(person.id);
  if (!shouldGreet({ person, today, seenOn })) return null;

  return (
    <GreetingModal
      person={person}
      onClose={() => {
        markSeen(person.id, today);
        setClosed(closedKey);
      }}
    />
  );
};
